import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_CONTROL_SELECT_OPEN_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { SettingsDrawerHeader } from '@/components/settings/SettingsDrawerHeader'
import { ListPagination } from '@/components/shared/ListPagination'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { EntityPicker } from '@/components/shared/EntityPicker'
import { ConfirmDeleteDialog } from '@/components/shared'
import { useListParams } from '@/hooks/useListParams'
import { settingsApi } from '@/services/settings'
import {
  useAttachmentRules,
  useCreateAttachmentRule,
  useUpdateAttachmentRule,
  useDeleteAttachmentRule,
} from '@/hooks/useSettings'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import type {
  AttachmentRule,
  AttachmentRuleRequest,
  AttachmentTarget,
} from '@/types'

const TARGETS: AttachmentTarget[] = ['quote', 'contract', 'job', 'invoice']
const COLUMN_COUNT = 6

const cardClass =
  'rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_-12px_rgba(38,45,66,0.1)]'

const drawerCardClass =
  'rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_-1px_rgba(38,45,66,0.05),0_1px_8px_-1px_rgba(38,45,66,0.1)]'

const fieldLabelClass = 'text-xs font-medium leading-4 text-[#171a20]'

const fieldInputClass = cn(
  'h-10 rounded-xl bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none placeholder:text-[#6c7285]',
  FORM_CONTROL_FOCUS_CLASS
)

const pickerTriggerClass = cn(
  'h-10 w-full justify-between rounded-xl bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none [&>span]:font-normal [&>svg]:text-[#171a20] [&>svg]:opacity-100',
  FORM_CONTROL_SELECT_OPEN_CLASS
)

const drawerClass = 'tf-right-drawer-short min-h-0 max-h-dvh *:min-w-0'

const dialogBtnOutlineClass =
  'rounded-xl border-[#e7e9ef] bg-white px-3 py-3 text-sm font-medium leading-none text-[#171a20] shadow-none hover:bg-[#f8f9fb]'

const dialogBtnPrimaryClass =
  'rounded-xl bg-[#ff5a1f] px-3 py-3 text-sm font-medium leading-none text-white shadow-none hover:bg-[#e6511c]'

const switchClass =
  'h-6 w-10 border-0 data-[state=checked]:bg-[#10b981] data-[state=unchecked]:bg-[#8f95a6] [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-4 [&>span]:data-[state=unchecked]:translate-x-0.5'

const thClass =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-left text-[10px] font-medium uppercase text-[#3d434e]'

const tdClass =
  'h-16 border-b border-[#e7e9ef] px-3 py-2 align-middle text-sm text-[#171a20]'

const actionBtnClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-1.5 text-[#272c36] transition-colors group-hover:bg-[#fbfbfc] disabled:cursor-not-allowed disabled:opacity-50'
const accentActionBtnClass =
  'hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] group-hover:hover:bg-[#fff1ea]'
const destructiveActionBtnClass =
  'hover:border-[#d63b33] hover:bg-[#fde9e8] hover:text-[#d63b33] group-hover:hover:bg-[#fde9e8]'
interface FormState {
  name: string
  attach_to: AttachmentTarget[]
  reusable_document_id: string
  is_active: boolean
}

const EMPTY: FormState = {
  name: '',
  attach_to: [],
  reusable_document_id: '',
  is_active: true,
}

function StatusTag({ active }: { active: boolean }) {
  const { t } = useTranslation('settings')

  return (
    <span
      className='inline-flex items-center justify-center rounded-lg px-1.5 py-1 text-xs font-medium leading-4'
      style={
        active
          ? { backgroundColor: '#D1FAE5', color: '#065F46' }
          : { backgroundColor: '#e6e6e6', color: '#1e1e1e' }
      }
    >
      {active ? t('common.active') : t('common.inactive')}
    </span>
  )
}

function TargetBadge({ target }: { target: AttachmentTarget }) {
  const { t } = useTranslation('settings')

  return (
    <span className='inline-flex items-center rounded-lg bg-[#f4f5f8] px-1.5 py-1 text-xs font-medium leading-4 text-[#171a20]'>
      {t(`attachmentRules.docTypes.${target}`)}
    </span>
  )
}

// 2.11.20 — Attachment picker visibility rules. Embedded view follows the Figma
// documents tab: standalone card + right-side drawer for create/edit.
export function AttachmentRulesPanel({
  embedded: _embedded = false,
}: {
  embedded?: boolean
}) {
  const { t } = useTranslation('settings')
  const { page, setPage, limit, setLimit, query } = useListParams()
  const { data, isLoading, isError } = useAttachmentRules(query)
  const create = useCreateAttachmentRule()
  const update = useUpdateAttachmentRule()
  const remove = useDeleteAttachmentRule()

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<AttachmentRule | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [toDelete, setToDelete] = useState<AttachmentRule | null>(null)

  const rows = useMemo(() => data?.data ?? [], [data])
  const set = (patch: Partial<FormState>) => setForm(f => ({ ...f, ...patch }))

  const toggleTarget = (target: AttachmentTarget) =>
    setForm(f => ({
      ...f,
      attach_to: f.attach_to.includes(target)
        ? f.attach_to.filter(value => value !== target)
        : [...f.attach_to, target],
    }))

  function openCreate() {
    setEditing(null)
    setForm(EMPTY)
    setErrors({})
    setOpen(true)
  }

  function openEdit(rule: AttachmentRule) {
    setEditing(rule)
    setForm({
      name: rule.name,
      attach_to: rule.attach_to ?? [],
      reusable_document_id: rule.reusable_document_id,
      is_active: rule.is_active,
    })
    setErrors({})
    setOpen(true)
  }

  function submit() {
    const next: Record<string, string> = {}

    if (!form.name.trim())
      next.name = t('attachmentRules.validation.nameRequired')
    if (form.attach_to.length === 0)
      next.attach_to = t('attachmentRules.validation.attachToRequired')
    if (!form.reusable_document_id)
      next.reusable_document_id = t(
        'attachmentRules.validation.documentRequired'
      )

    setErrors(next)
    if (Object.keys(next).length) return

    const body: AttachmentRuleRequest = { ...form }
    const onSuccess = () => {
      toast({
        variant: 'success',
        title: editing
          ? t('attachmentRules.toast.updated')
          : t('attachmentRules.toast.created'),
      })
      setOpen(false)
    }
    const onError = (error: unknown) =>
      toast({ variant: 'destructive', title: getApiErrorMessage(error) })

    if (editing) update.mutate({ id: editing.id, body }, { onSuccess, onError })
    else create.mutate(body, { onSuccess, onError })
  }

  function confirmDelete() {
    if (!toDelete) return

    remove.mutate(toDelete.id, {
      onSuccess: () => {
        toast({ variant: 'success', title: t('attachmentRules.toast.deleted') })
        setToDelete(null)
      },
      onError: error =>
        toast({ variant: 'destructive', title: getApiErrorMessage(error) }),
    })
  }

  const addButton = (
    <Button
      className='h-10 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 text-sm font-medium leading-none text-white shadow-none hover:bg-[#e6511c]'
      onClick={openCreate}
    >
      <Plus className='mr-2 size-4' />
      {t('attachmentRules.addRule')}
    </Button>
  )

  const listSection = (
    <section className={cardClass}>
      <div className='flex flex-col gap-6 py-6'>
        <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
          <div className='min-w-0 flex-1'>
            <div className='flex min-w-0 items-center border-l-4 border-[#ff5a1f] px-6 py-1'>
              <h2 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
                {t('attachmentRules.title')}
              </h2>
            </div>
          </div>

          <div className='mx-4 sm:mx-5 lg:mx-6'>{addButton}</div>
        </div>

        <div className='px-4 sm:px-5 lg:px-6'>
          <div className='overflow-x-auto'>
            <table className='w-full min-w-[820px] caption-bottom text-sm'>
              <TableHeader>
                <TableRow className='border-0'>
                  <TableHead className={cn(thClass, 'w-[320px]')}>
                    {t('attachmentRules.columns.name')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[200px]')}>
                    {t('attachmentRules.columns.attachTo')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[200px]')}>
                    {t('attachmentRules.columns.document')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[120px] text-center')}>
                    {t('common.status')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[188px]')}>
                    {t('attachmentRules.columns.lastUpdated')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[96px] text-right')}>
                    <span>{t('common.actions')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {isLoading &&
                  Array.from({ length: 3 }).map((_, index) => (
                    <TableRow key={index} className='border-0'>
                      <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                        <Skeleton className='h-6 w-full' />
                      </TableCell>
                    </TableRow>
                  ))}

                {isError && !isLoading && (
                  <TableRow className='border-0'>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={cn(
                        tdClass,
                        'py-10 text-center text-destructive'
                      )}
                    >
                      {t('attachmentRules.loadError')}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && !isError && rows.length === 0 && (
                  <TableRow className='border-0'>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={cn(
                        tdClass,
                        'py-10 text-center text-muted-foreground'
                      )}
                    >
                      {t('attachmentRules.empty')}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading &&
                  !isError &&
                  rows.map(rule => {
                    return (
                      <TableRow key={rule.id} className='group border-0'>
                        <TableCell className={cn(tdClass)}>
                          <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                            {rule.name}
                          </span>
                        </TableCell>
                        <TableCell className={cn(tdClass)}>
                          <div className='flex flex-wrap gap-2'>
                            {(rule.attach_to ?? []).map(target => (
                              <TargetBadge key={target} target={target} />
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className={cn(tdClass)}>
                          <span className='text-sm leading-[1.4] text-[#171a20]'>
                            {rule.reusable_document?.name ??
                              rule.reusable_document_id}
                          </span>
                        </TableCell>
                        <TableCell className={cn(tdClass, 'text-center')}>
                          <StatusTag active={rule.is_active} />
                        </TableCell>
                        <TableCell className={cn(tdClass)}>
                          <span className='whitespace-nowrap text-sm leading-[1.4] text-[#171a20]'>
                            {rule.updated_at
                              ? formatDate(rule.updated_at)
                              : '—'}
                          </span>
                        </TableCell>
                        <TableCell className={cn(tdClass)}>
                          <div className='flex items-center justify-end gap-2'>
                            <TruncatedTextTooltip
                              content={t('common:actions.edit')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={cn(
                                  actionBtnClass,
                                  accentActionBtnClass
                                )}
                                aria-label={t('common:actions.edit')}
                                onClick={() => openEdit(rule)}
                              >
                                <Pencil className='size-4' />
                              </button>
                            </TruncatedTextTooltip>
                            <TruncatedTextTooltip
                              content={t('common:actions.delete')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={cn(
                                  actionBtnClass,
                                  destructiveActionBtnClass
                                )}
                                aria-label={t('common:actions.delete')}
                                onClick={() => setToDelete(rule)}
                              >
                                <Trash2 className='size-4' />
                              </button>
                            </TruncatedTextTooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </table>
          </div>
        </div>
      </div>

      <ListPagination
        metadata={data?.metadata}
        page={page}
        pageSize={limit}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
        className='px-4 sm:px-5'
        showShowingRange={false}
      />
    </section>
  )

  return (
    <>
      {listSection}

      <Dialog
        open={open}
        onOpenChange={next => {
          if (!next && (create.isPending || update.isPending)) return
          setOpen(next)
        }}
      >
        <DialogContent
          side='right'
          showCloseButton={false}
          className={drawerClass}
        >
          <SettingsDrawerHeader
            title={
              editing
                ? t('attachmentRules.dialog.editTitle')
                : t('attachmentRules.dialog.addTitle')
            }
            closeDisabled={create.isPending || update.isPending}
            titleClassName='text-base'
          />

          <div className='flex flex-1 flex-col overflow-y-auto border-t border-[#e7e9ef] bg-[#f4f5f8]'>
            <div className='flex flex-1 flex-col gap-4 px-6 py-6'>
              <div className={drawerCardClass}>
                <div className='h-[42px] px-0 pb-2 pt-4'>
                  <div className='flex items-center border-l-4 border-[#ff5a1f] px-4'>
                    <h3 className='font-heading text-sm font-semibold lowercase leading-[1.2] text-[#3d434e] first-letter:uppercase'>
                      {t('attachmentRules.dialog.informationTitle')}
                    </h3>
                  </div>
                </div>

                <div
                  key={editing?.id ?? 'create'}
                  className='flex flex-col gap-6 px-4 pb-6 pt-2'
                >
                  <div className='flex flex-col gap-1'>
                    <Label htmlFor='ar-name' className={fieldLabelClass}>
                      {t('attachmentRules.fields.name')}
                      <span className='text-[#d63b33]'> *</span>
                    </Label>
                    <Input
                      id='ar-name'
                      value={form.name}
                      maxLength={100}
                      onChange={event => {
                        set({ name: event.target.value })
                        setErrors(current => ({ ...current, name: '' }))
                      }}
                      placeholder={t('users.fields.enterHere')}
                      className={cn(
                        fieldInputClass,
                        errors.name && 'border border-destructive'
                      )}
                    />
                    {errors.name && (
                      <p className='text-sm text-destructive'>{errors.name}</p>
                    )}
                  </div>

                  <div className='flex flex-col gap-1'>
                    <Label className={fieldLabelClass}>
                      {t('attachmentRules.fields.attachTo')}
                      <span className='text-[#d63b33]'> *</span>
                    </Label>
                    <div className='flex flex-wrap gap-2'>
                      {TARGETS.map(target => {
                        const active = form.attach_to.includes(target)

                        return (
                          <button
                            key={target}
                            type='button'
                            aria-pressed={active}
                            onClick={() => {
                              toggleTarget(target)
                              setErrors(current => ({
                                ...current,
                                attach_to: '',
                              }))
                            }}
                            className={cn(
                              'inline-flex min-h-10 items-center rounded-xl border px-3 py-2.5 text-sm font-medium leading-[1.4] shadow-[0_1px_4px_rgba(38,45,66,0.05)] transition-colors',
                              active
                                ? 'border-[#059669] bg-[#ecfdf5] text-[#171a20]'
                                : 'border-[#e7e9ef] bg-white text-[#171a20] hover:bg-[#fbfbfc]'
                            )}
                          >
                            {t(`attachmentRules.docTypes.${target}`)}
                          </button>
                        )
                      })}
                    </div>
                    {errors.attach_to && (
                      <p className='text-sm text-destructive'>
                        {errors.attach_to}
                      </p>
                    )}
                  </div>

                  <div className='flex flex-col gap-1'>
                    <Label className={fieldLabelClass}>
                      {t('attachmentRules.fields.reusableDocument')}
                      <span className='text-[#d63b33]'> *</span>
                    </Label>
                    <EntityPicker
                      queryKey='reusable-documents'
                      fetcher={params =>
                        settingsApi.listReusableDocuments({
                          search: params.search,
                          limit: params.limit,
                          page: params.page,
                        })
                      }
                      getOptionLabel={document => document.name}
                      getOptionValue={document => document.id}
                      value={form.reusable_document_id}
                      onChange={value => {
                        set({ reusable_document_id: value })
                        setErrors(current => ({
                          ...current,
                          reusable_document_id: '',
                        }))
                      }}
                      initialLabel={
                        editing?.reusable_document_id ===
                        form.reusable_document_id
                          ? editing.reusable_document?.name
                          : undefined
                      }
                      placeholder={t(
                        'attachmentRules.placeholders.selectDocument'
                      )}
                      className={cn(
                        pickerTriggerClass,
                        errors.reusable_document_id &&
                          'border border-destructive'
                      )}
                    />
                    {errors.reusable_document_id && (
                      <p className='text-sm text-destructive'>
                        {errors.reusable_document_id}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className='flex min-h-6 items-center gap-3'>
                      <Label
                        htmlFor='ar-active'
                        className='flex-1 text-sm font-medium leading-[1.4] text-[#171a20]'
                      >
                        {t('attachmentRules.fields.active')}
                      </Label>
                      <Switch
                        id='ar-active'
                        checked={form.is_active}
                        onCheckedChange={value => set({ is_active: value })}
                        className={switchClass}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='tf-right-drawer-footer justify-end'>
            <Button
              type='button'
              variant='outline'
              className={dialogBtnOutlineClass}
              onClick={() => setOpen(false)}
              disabled={create.isPending || update.isPending}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type='button'
              className={dialogBtnPrimaryClass}
              onClick={submit}
              disabled={create.isPending || update.isPending}
            >
              {t('common:actions.save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={next => !remove.isPending && !next && setToDelete(null)}
        title={t('attachmentRules.dialog.deleteTitle')}
        description={t('attachmentRules.dialog.deleteMessage')}
        confirmLabel={t('attachmentRules.dialog.deleteConfirm')}
        onConfirm={confirmDelete}
        busy={remove.isPending}
      />
    </>
  )
}

export default AttachmentRulesPanel
